import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { GraduationCap, Sparkles, BookOpen, Music2, DollarSign, Upload, ArrowRight, Check, Mic2, Shield } from 'lucide-react';

export default function ForArtists() {
  const { user } = useApp();
  const cta = user ? '/reapercast' : '/signup/musician';
  const ctaLabel = user ? 'Open my dashboard' : 'Start free as an artist';
  return (
    <div className="max-w-[1100px] mx-auto" data-testid="landing-artists">
      <Hero />
      <PerksGrid />
      <StorageNote />
      <FinalCta href={cta} label={ctaLabel} />
    </div>
  );
}

function Hero() {
  return (
    <header className="py-10 md:py-16">
      <div className="chip"><Music2 className="w-3 h-3" /> For artists</div>
      <h1 className="font-display text-[40px] sm:text-[56px] md:text-[72px] leading-[0.95] mt-4 max-w-[960px]">
        Music is the <span className="text-[#ff2e63]">main act</span>. Everything else supports the song.
      </h1>
      <p className="text-white/70 text-[16px] md:text-[18px] mt-5 max-w-[680px] leading-relaxed">
        Release, distribute, get paid, learn the business, and host your video catalog — one operating system for indie artists. Free to start.
      </p>
    </header>
  );
}

const PERKS = [
  { Icon: Music2,        color: '#22d3ee', title: 'Music Reel + Artists page',  desc: 'Your tracks land in a TikTok-style listener feed and a public profile. Drop a track, gain a fan.' },
  { Icon: GraduationCap, color: '#ffcf52', title: 'Music Biz Academy',          desc: '7 tracks · 38 lessons. Finish a track → unlock an AI Specialist coach. Shareable certificates.' },
  { Icon: Sparkles,      color: '#a78bfa', title: '7 AI Specialists',           desc: 'Rights Coach, Royalty Auditor, Licensing Advisor, Publisher Agent, and more. Chat anytime.' },
  { Icon: BookOpen,      color: '#34d399', title: 'Knowledge Hub (70% to you)', desc: 'Self-publish ebooks, audiobooks, podcasts, workbooks. Keep 70%. Way better than Amazon.' },
  { Icon: DollarSign,    color: '#f472b6', title: 'Income Discovery + advisor', desc: '15+ income streams catalogued. AI advisor picks your top 3 to chase first.' },
  { Icon: Upload,        color: '#ff7a00', title: 'VidCreForge bundled free',   desc: 'Music videos, BTS, livestreams. 1GB free hosting. Upgrade for more.' },
];

function PerksGrid() {
  return (
    <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-7">
      {PERKS.map(p => (
        <div key={p.title} className="card-shell p-5 hover-lift">
          <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: p.color + '22', color: p.color }}><p.Icon className="w-5 h-5" /></div>
          <h3 className="font-display text-[16px] mt-3">{p.title}</h3>
          <p className="text-[12.5px] text-white/60 mt-1.5 leading-relaxed">{p.desc}</p>
        </div>
      ))}
    </section>
  );
}

function StorageNote() {
  return (
    <section className="card-shell p-5 md:p-6 mb-7" data-testid="landing-storage-note">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'rgba(34,211,238,0.18)', color: '#22d3ee' }}><Shield className="w-5 h-5" /></span>
        <div className="min-w-0">
          <h3 className="font-display text-[18px]">Same generous video hosting on every account</h3>
          <p className="text-white/65 text-[13px] mt-1 leading-relaxed">Every ReaperCast account ships with <span className="text-white font-semibold">1 GB of free video hosting</span>. Need more? Upgrade once — it scales with you.</p>
          <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-[12px]">
            {[['Free', '1 GB'], ['Plus · $4.99', '25 GB'], ['Pro · $9.99', '250 GB'], ['Studio · $19.99', '2 TB']].map(([n, s]) => (
              <li key={n} className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2">
                <div className="text-white/55 text-[10.5px] uppercase tracking-wider">{n}</div>
                <div className="font-semibold mt-0.5">{s}</div>
              </li>
            ))}
          </ul>
          <p className="text-[11.5px] text-white/45 mt-2.5">You only pay if you need the storage — your music stays free.</p>
        </div>
      </div>
    </section>
  );
}

export function FinalCta({ href, label }) {
  return (
    <section className="card-shell p-6 md:p-8 mb-12 text-center" style={{ background: 'linear-gradient(135deg, rgba(255,46,99,0.18), rgba(167,139,250,0.12))', borderColor: 'rgba(255,46,99,0.45)' }}>
      <Mic2 className="w-7 h-7 mx-auto text-[#ff2e63]" />
      <h2 className="font-display text-[28px] md:text-[36px] mt-3">Ship your first release on the right rails.</h2>
      <Link to={href} className="btn-primary mt-5 inline-flex" data-testid="landing-cta">
        {label} <ArrowRight className="w-4 h-4" />
      </Link>
      <p className="text-[11.5px] text-white/45 mt-3 inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-[#34d399]" /> No credit card · Free forever tier · Upgrade anytime</p>
    </section>
  );
}
